import { Injectable, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import { ParamsType } from './models/params.type';
import { ActivatedRoute, Router } from '@angular/router';
import { paramsConfig } from './params.config';
import { ParamsEnum } from './models/params.enum';
import { startWith, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ParamsService implements OnDestroy {

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  private readonly _params: WritableSignal<Partial<ParamsType>> = signal({});

  private routeSubscription?: Subscription;

  private readonly savedStates = new Map<string, Partial<ParamsType>>();

  public ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  public get params(): Signal<Partial<ParamsType>> {
    return this._params;
  }

  public set params(params: Partial<ParamsType>) {
    const currentParams = { ...this.route.snapshot.queryParams };

    const newParams: Record<string, unknown> = {
      ...currentParams,
      ...params,
    };

    this.applyParams(newParams);
  }

  public replaceParams(params: Partial<ParamsType>): void {
    this.applyParams({ ...params });
  }

  public initialize(): void {
    this.routeSubscription = this.route.queryParams
      .pipe(
        startWith(this.route.snapshot.queryParams),
      )
      .subscribe(queryParams => {
        const params = this.getTypedParams(queryParams);
        this._params.set(params);
      });
  }

  public saveState(key: string, params: Partial<ParamsType>): void {
    this.savedStates.set(key, { ...params });
  }

  public restoreState(key: string): Partial<ParamsType> | undefined {
    return this.savedStates.get(key);
  }

  public isInvalid(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  public amount(ignoreParams?: string[]): number {
    let amount = 0;

    Object.keys(this.params()).forEach(key => {
      if (ignoreParams && ignoreParams.includes(key))
        return;

      const value = this.params()[key as keyof ParamsType];
      if (value !== undefined)
        amount++;
    });

    return amount;
  }

  private applyParams(params: Record<string, unknown>): void {
    const newParams = { ...params };

    for (const key in newParams) {
      if (Object.prototype.hasOwnProperty.call(paramsConfig, key) && this.isInvalid(newParams[key as keyof ParamsType]))
        delete newParams[key];
    }

    this._params.set(this.getTypedParams(newParams));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: newParams,
    });
  }

  private getTypedParams(queryParams: Record<string, unknown>): Partial<ParamsType> {
    const typedParams: Partial<ParamsType> = {};

    for (const key in queryParams) {
      if (!Object.prototype.hasOwnProperty.call(paramsConfig, key))
        continue;

      const valueType = paramsConfig[key as keyof typeof paramsConfig] as ParamsEnum;

      const value = queryParams[key];

      if (value === undefined)
        continue;

      switch (valueType) {
        case ParamsEnum.STRING: {
          if (typeof value === 'string')
            (typedParams as Record<string, unknown>)[key] = value;
          break;
        }

        case ParamsEnum.NUMBER: {
          const numberValue = Number(value);
          if (!isNaN(numberValue))
            (typedParams as Record<string, unknown>)[key] = +numberValue;
          break;
        }

        case ParamsEnum.BOOLEAN: {
          if (value === 'true' || value === 'false')
            (typedParams as Record<string, unknown>)[key] = value === 'true';
          break;
        }

        case ParamsEnum.DATE: {
          let date: Date;
          if (value instanceof Date) {
            date = value;
          } else if (typeof value === 'string') {
            if (value.includes('/') && value.split('/').length === 3) {
              const [day, month, year] = value.split('/');
              date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            } else {
              date = new Date(value);
            }
          } else {
            break;
          }

          if (!isNaN(date.getTime())) {
            (typedParams as Record<string, unknown>)[key] = date;
          }
          break;
        }

        case ParamsEnum.ARRAY_STRING: {
          const stringArray = this.processValue(value as string, v => typeof v === 'string');
          if (stringArray && stringArray.length)
            (typedParams as Record<string, unknown>)[key] = stringArray;
          break;
        }

        case ParamsEnum.ARRAY_NUMBER: {
          const numberArray = this.processValue(value as string, v => !isNaN(Number(v)), Number);
          if (numberArray && numberArray.length)
            (typedParams as Record<string, unknown>)[key] = numberArray;
          break;
        }

        case ParamsEnum.ARRAY_BOOLEAN: {
          const booleanArray = this.processValue(value as string, v => v === 'true' || v === 'false', v => v === 'true');
          if (booleanArray && booleanArray.length)
            (typedParams as Record<string, unknown>)[key] = booleanArray;
          break;
        }
      }
    }

    return typedParams;
  }

  private processValue<T>(value: T[] | string, validate: (item: T) => boolean, transform?: (item: T) => T): T[] | undefined {
    if (Array.isArray(value)) {
      const validArray = value.filter(validate);
      return transform ? validArray.map(transform) : validArray;
    }

    const delimiters = [',', ';'];
    for (const delimiter of delimiters) {
      if (value.includes(delimiter)) {
        const array = value.split(delimiter).map(item => item.trim() as unknown as T).filter(validate);
        return transform ? array.map(transform) : array;
      }
    }

    return undefined;
  }

}
